import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import DashboardMainContainer from "../../../components/common/Containers/DashboardMainContainer";
import {
  AdminPageTitle,
  AdminMetadataText,
  AdminStatusBadge,
  AdminHeaderAction,
  AdminAlertText,
  AdminMainText,
  AdminChannelBadge,
  AdminDetailFieldLabel,
  adminWizardTextareaClasses,
} from "../../../sections/AdminDashboard/shared/adminUx";
import {
  CUSTOMER_INBOX_CONTENT_CLASS,
  CUSTOMER_LIST_PAGE_TITLE_CLASS,
  CUSTOMER_DETAIL_MAIN_HEADER_CLASS,
} from "../../../sections/CustomerDashboard/shared/customerLayoutSpec";
import { runTextPipeline, validateFile } from "../../../utils/contentSafety";
import Alerts from "../../../components/common/Alerts/Alerts";

const CustomerTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ticketFromState = location.state?.ticket;

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [replyFile, setReplyFile] = useState(null);

  const formatDate = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return (
      d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) +
      ", " +
      d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })
    );
  };

  const ticketStatusKey = (status) => (status === "Resolved" ? "Approved" : status);

  const priorityStatusKey = (priority) => {
    if (priority === "High") return "Escalated";
    if (priority === "Medium") return "Warning";
    if (priority === "Low") return "Active";
    return priority;
  };

  const formatChannelDisplay = (channel) => {
    if (!channel) return "Web";
    let s = String(channel).trim().replace(/\s+form$/i, "").trim();
    return s || "Web";
  };

  const isSupportSender = (sender) => sender === "Support" || sender === "Support Team";
  const isCustomerSender = (sender) => sender === "Customer";

  const buildTicketWithMessages = (base) => {
    const now = new Date();
    const initialMessage =
      base.description ||
      "I'm having trouble with my account. The password reset link keeps expiring before I can use it. Could you please help?";
    const messages = [
      {
        id: "msg1",
        sender: "Customer",
        senderName: base.customerName || "You",
        channel: "Web form",
        time: "09:31",
        timestamp: new Date(now.getTime() - 3 * 60 * 60 * 1000),
        message: initialMessage,
      },
      {
        id: "msg2",
        sender: "Support Team",
        senderName: "Support Team",
        channel: "Email",
        time: "09:45",
        timestamp: new Date(now.getTime() - 2.75 * 60 * 60 * 1000),
        message:
          "Thank you for contacting us. We've sent a new password reset link to your email. Please try again within the next 24 hours. If you still have issues, reply to this ticket.",
      },
      {
        id: "msg3",
        sender: "Customer",
        senderName: base.customerName || "You",
        channel: "Web form",
        time: "10:15",
        timestamp: new Date(now.getTime() - 2 * 60 * 60 * 1000),
        message: "The new link worked. Thank you for the quick help!",
      },
    ];
    return {
      ...base,
      description: base.description || initialMessage,
      attachments: base.attachments || [],
      messages,
    };
  };

  useEffect(() => {
    const fetchTicket = async () => {
      setLoading(true);
      try {
        if (ticketFromState) {
          setTicket(buildTicketWithMessages(ticketFromState));
        } else {
          await new Promise((r) => setTimeout(r, 400));
          setTicket(
            buildTicketWithMessages({
              ticketId: ticketId || "TCK-19281",
              subject: "Password reset not working",
              priority: "High",
              category: "Account Access",
              status: "Open",
              createdDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
              lastUpdated: new Date(Date.now() - 1 * 60 * 60 * 1000),
              customerName: "You",
            })
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [ticketId, ticketFromState]);

  const handleSendReply = () => {
    if (!replyText.trim()) return;
    if (ticket?.status === "Closed") return;
    const result = runTextPipeline(replyText.trim(), "support");
    if (!result.valid) {
      Alerts.error(result.message);
      return;
    }
    const now = new Date();
    const newMsg = {
      id: `msg-${Date.now()}`,
      sender: "Customer",
      senderName: "You",
      channel: "Web form",
      time: now.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }),
      timestamp: now,
      message: result.normalized ?? replyText,
      attachments: replyFile ? [{ id: "new1", name: replyFile.name, type: "File", size: "" }] : undefined,
    };
    setTicket((prev) => {
      const shouldReopen =
        prev.status === "Awaiting Reply" || prev.status === "Resolved";
      return {
        ...prev,
        messages: [...(prev.messages || []), newMsg],
        lastUpdated: now,
        ...(shouldReopen ? { status: "In Progress" } : {}),
      };
    });
    setReplyText("");
    setReplyFile(null);
  };

  const canReplyToTicket = ticket?.status !== "Closed";

  const handleReplyFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setReplyFile(null);
      return;
    }
    const isImage = (file.type || "").startsWith("image/");
    const result = validateFile(file, isImage ? "image" : "document");
    if (!result.valid) {
      Alerts.error(result.message);
      e.target.value = "";
      return;
    }
    setReplyFile(file);
    e.target.value = "";
  };

  const threadMessages = useMemo(() => {
    if (!ticket?.messages) return [];
    const sorted = [...ticket.messages].sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return ta - tb;
    });
    if (!ticket.description) return sorted;
    let skippedInitialCustomer = false;
    return sorted.filter((m) => {
      if (isSupportSender(m.sender)) return true;
      if (isCustomerSender(m.sender) && !skippedInitialCustomer) {
        skippedInitialCustomer = true;
        return false;
      }
      return true;
    });
  }, [ticket?.messages, ticket?.description]);

  const renderMessageBubble = (message, msgIdx = 0) => {
    if (!ticket) return null;
    const isSupport = isSupportSender(message.sender);
    const senderDisplay = isSupport
      ? "Support Team"
      : message.senderName || message.sender || "You";
    const messageTimestamp = message.timestamp
      ? formatDateTime(message.timestamp)
      : message.time || "—";
    const channelBadge = formatChannelDisplay(message.channel || ticket.channel || "Web form");
    const avatarSrc =
      ticket.customer?.profilePicture || `https://i.pravatar.cc/100?img=${(msgIdx % 70) + 1}`;

    return (
      <div
        key={message.id || `msg-${msgIdx}`}
        className={`flex ${isSupport ? "justify-end" : "justify-start"}`}
      >
        <div
          className={`min-w-[320px] max-w-[85%] rounded-2xl px-5 py-4 ${
            isSupport
              ? "bg-primaryblue text-white rounded-br-md"
              : "bg-mistygray text-offblack rounded-bl-md"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              {isSupport ? (
                <div className="w-8 h-8 rounded-full flex-shrink-0 bg-white/35 flex items-center justify-center ring-1 ring-white/50">
                  <i className="fi fi-rs-headphones text-white text-sm" aria-hidden />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden bg-white border-2 border-white shadow-sm ring-1 ring-offblack/10">
                  <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <AdminMainText className={`text-sm ${isSupport ? "text-white" : "text-offblack"}`}>
                {senderDisplay}
              </AdminMainText>
              <AdminChannelBadge label={channelBadge} onSupport={isSupport} />
            </div>
            <AdminMetadataText
              className={`whitespace-nowrap ${isSupport ? "text-white/80" : "text-mediumgray"}`}
            >
              {messageTimestamp}
            </AdminMetadataText>
          </div>
          <AdminMainText
            className={`text-sm leading-relaxed whitespace-pre-wrap block ${
              isSupport ? "text-white" : "text-offblack"
            }`}
          >
            {message.message}
          </AdminMainText>
          {message.attachments && message.attachments.length > 0 && (
            <div className={`mt-4 rounded-xl p-4 ${isSupport ? "bg-white/15" : "bg-lightgray/80"}`}>
              <AdminMetadataText
                className={`block mb-2 ${isSupport ? "text-white/90" : "text-offblack"}`}
              >
                Attachments
              </AdminMetadataText>
              <div className="flex flex-wrap gap-2">
                {message.attachments.map((att) => (
                  <a
                    key={att.id || att.name}
                    href={att.url || "#"}
                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 flex-shrink-0 max-w-full ${
                      isSupport
                        ? "bg-white/20 hover:bg-white/30"
                        : "bg-white border border-lightgray hover:bg-mistygray"
                    } transition-colors`}
                  >
                    <i
                      className={`fi fi-rs-document flex-shrink-0 text-sm ${
                        isSupport ? "text-white" : "text-primaryblue"
                      }`}
                      aria-hidden
                    />
                    <AdminMainText
                      className={`text-sm truncate min-w-0 ${isSupport ? "text-white" : "text-offblack"}`}
                    >
                      {att.name}
                    </AdminMainText>
                    {att.type ? (
                      <AdminMetadataText
                        className={`flex-shrink-0 ${isSupport ? "text-white/80" : "text-mediumgray"}`}
                      >
                        · {att.type}
                        {att.size ? ` · ${att.size}` : ""}
                      </AdminMetadataText>
                    ) : null}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardMainContainer className="lg:px-5 lg:pb-10 !bg-white">
        <div className="flex min-h-[40vh] items-center justify-center">
          <AdminMetadataText className="text-offblack">Loading ticket...</AdminMetadataText>
        </div>
      </DashboardMainContainer>
    );
  }

  if (!ticket) {
    return (
      <DashboardMainContainer className="lg:px-5 lg:pb-10 !bg-white">
        <AdminAlertText className="text-alertred mb-4">Ticket not found.</AdminAlertText>
        <AdminHeaderAction onClick={() => navigate("/customer-dashboard/tickets/open")}>
          Back to tickets
        </AdminHeaderAction>
      </DashboardMainContainer>
    );
  }

  return (
    <DashboardMainContainer className="lg:px-5 lg:pb-10 !bg-white">
      <div className={`${CUSTOMER_INBOX_CONTENT_CLASS} space-y-6`}>
        <DashboardMainContainer className={CUSTOMER_DETAIL_MAIN_HEADER_CLASS}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <AdminPageTitle className={CUSTOMER_LIST_PAGE_TITLE_CLASS}>{ticket.subject}</AdminPageTitle>
              <div className="flex items-center gap-3 flex-wrap">
                <AdminMetadataText className="text-offblack">
                  Created {formatDate(ticket.createdDate)}
                </AdminMetadataText>
                <AdminStatusBadge
                  status={ticketStatusKey(ticket.status)}
                  label={ticket.status || "—"}
                />
                <AdminStatusBadge
                  status={priorityStatusKey(ticket.priority)}
                  label={`${ticket.priority || "—"} priority`}
                />
              </div>
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className="!bg-white">
          <div className="mt-6 lg:px-4 space-y-8">
            {ticket.description &&
              renderMessageBubble(
                {
                  id: "initial-request",
                  sender: "Customer",
                  senderName: ticket.customerName || "You",
                  channel: ticket.channel || "Web form",
                  timestamp: ticket.createdDate,
                  message: ticket.description,
                  attachments: ticket.attachments,
                },
                0
              )}

            {threadMessages.map((message, msgIdx) => renderMessageBubble(message, msgIdx + 1))}

            {canReplyToTicket && (
              <div className="rounded-xl border border-lightgray bg-white p-5 mt-4">
                <AdminDetailFieldLabel className="!text-sm !mb-3 block">
                  Reply to ticket
                </AdminDetailFieldLabel>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply here..."
                  className={`${adminWizardTextareaClasses(false)} min-h-[120px]`}
                  style={{ resize: "vertical" }}
                  rows={5}
                />
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-lightgray bg-mistygray px-3 py-2 text-sm text-offblack hover:border-primaryblue hover:bg-pastalblue transition-colors">
                    <i className="fi fi-rs-paperclip text-mediumgray" aria-hidden />
                    <span>Attach file</span>
                    <input type="file" onChange={handleReplyFileChange} className="hidden" />
                  </label>
                  <AdminHeaderAction
                    onClick={handleSendReply}
                    disabled={!replyText.trim()}
                  >
                    Send reply
                  </AdminHeaderAction>
                  {replyFile && (
                    <AdminMetadataText className="text-mediumgray flex items-center gap-1">
                      <i className="fi fi-rs-check-circle text-freshgreen" aria-hidden />
                      {replyFile.name}
                    </AdminMetadataText>
                  )}
                </div>
              </div>
            )}

            {ticket.status === "Resolved" && canReplyToTicket && (
              <div className="rounded-xl border border-lightgray bg-white p-5 mt-4">
                <AdminDetailFieldLabel className="!text-sm !mb-2 block">
                  Ticket resolved
                </AdminDetailFieldLabel>
                <AdminMetadataText className="text-mediumgray block">
                  If you still need help, send a reply below before the reopening period ends to reopen this ticket.
                </AdminMetadataText>
              </div>
            )}
          </div>
        </DashboardMainContainer>
      </div>
    </DashboardMainContainer>
  );
};

export default CustomerTicketDetail;
